// One seat in the circle: a character token, the player's name, and whatever the viewer may know
// about them. The server's projection has already decided what is in the view; this only draws it
// and never infers a secret from an absence.
//
// Each corner of the token means one thing: top-left poisoned, top-right dead, bottom-left drunk,
// bottom-right hand raised on the running vote or protected (never both: protection is night-only,
// votes are day-only).

import { useEffect, useState } from "react";
import {
  ArrowLeft, ArrowRight, BookOpen, Beer, Check, FlaskConical, Hand, Pencil, Shield, Skull, Trash2, X,
} from "lucide-react";
import type { GameView, SeatView } from "@cc/shared";
import { cn, findCharacter, teamColor, wikiUrl } from "../../lib/ui.js";

/** How long an armed Remove stays armed before it gives up and disarms itself. */
const REMOVE_CONFIRM_MS = 4000;

export type SeatDisplay = {
  seat: SeatView;
  view: GameView;
  isMe: boolean;
  /** Hand raised on the open nomination, if one is running. */
  handRaised: boolean;
  /** True once the vote clock has swept past this seat and its answer is final. */
  voteLocked: boolean;
  /**
   * Hand is up on the running vote: pending until the clock reaches them, locked in after. Either
   * way the seat glows.
   */
  hasVoteIn: boolean;
  /** The seat the clock hand is currently over. */
  isNominee: boolean;
  onClick?: () => void;
  onGuess?: () => void;
  /** Storyteller only: swap this seat with the one either side of it. */
  onMoveSeat?: (direction: -1 | 1) => void;
  /** Storyteller only: take this player out of the game entirely. */
  onRemove?: () => void;
};

/** Everything a Storyteller can currently see about this seat, as one sentence for the hover. */
function describeSeat(view: GameView, seat: SeatView): string {
  const character = findCharacter(view, seat.characterId);
  const parts: string[] = [];
  if (character) {
    parts.push(character.ability ? `${character.name} — ${character.ability}` : character.name);
  }
  if (seat.poisoned) parts.push("Poisoned.");
  if (seat.secretCharacterId) {
    // The disguise above is what everyone including the Storyteller's own token sees; this is the
    // one line that says it's a lie.
    const secretName = findCharacter(view, seat.secretCharacterId)?.name ?? seat.secretCharacterId;
    parts.push(`Secretly the ${secretName}.`);
  } else if (seat.drunk) {
    parts.push("Drunk.");
  }
  if (seat.protected) parts.push("Protected tonight.");
  if (seat.alignmentOverride) parts.push(`Turned ${seat.alignmentOverride}.`);
  if (!seat.alive) {
    parts.push(seat.deathCause === "executed" ? "Executed." : seat.deathCause === "exiled" ? "Exiled." : "Dead.");
    if (!seat.ghostVoteUsed) parts.push("Ghost vote unspent.");
  }
  if (!seat.connected) parts.push("Not connected.");
  return parts.length > 0 ? parts.join(" ") : seat.displayName;
}

export function Seat({
  seat, view, isMe, handRaised, voteLocked, hasVoteIn, isNominee, onClick, onGuess, onMoveSeat, onRemove,
}: SeatDisplay) {
  const [confirmRemove, setConfirmRemove] = useState(false);

  // An armed bin that stays armed is a trap for the next click, so it disarms on its own.
  useEffect(() => {
    if (!confirmRemove) return;
    const id = setTimeout(() => setConfirmRemove(false), REMOVE_CONFIRM_MS);
    return () => clearTimeout(id);
  }, [confirmRemove]);

  const character = findCharacter(view, seat.characterId);
  // The true card of a disguised seat (Drunk, Lunatic, Marionette), shown only in the corner badge
  // below — the main token stays the disguise for everyone, including the Storyteller, exactly as
  // the table believes it.
  const secretCard = seat.secretCharacterId ? findCharacter(view, seat.secretCharacterId) : null;
  // The Drunk's card marks a drunk seat; the others are always on show since they are never "drunk".
  const showSecretBadge = secretCard?.icon && (seat.secretCharacterId !== "drunk" || seat.drunk);
  const guess = findCharacter(view, seat.myGuess);
  // A guess only ever shows where the real answer is not already on screen — otherwise the pencil
  // note would sit next to the truth and just be noise.
  const showGuess = guess !== null && character === null;
  const token = character ?? (showGuess ? guess : null);
  // Only for a character actually on show; a private guess is not worth a lookup.
  const wiki = character ? wikiUrl(character) : null;
  const showSeatTools = onMoveSeat !== undefined || onRemove !== undefined;

  return (
    <div className="group flex w-28 flex-col items-center gap-1 select-none">
      <button
        type="button"
        onClick={onClick}
        className={cn(
          "relative grid size-20 place-items-center rounded-full border-2 bg-ink/80 transition",
          "hover:border-brass focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brass",
          seat.alive ? "border-ink-line" : "border-ink-line/40 opacity-60",
          // Rings rather than a border colour: the border is set inline from the character's team,
          // and an inline style always beats a class, so a border-based highlight never showed.
          isMe && "ring-2 ring-brass/70 ring-offset-2 ring-offset-ink",
          isNominee && "ring-2 ring-evil ring-offset-2 ring-offset-ink",
          // A glow rather than another ring (yours and the nominee's already use them), so it reads
          // across the circle.
          hasVoteIn &&
            "shadow-[0_0_10px_3px_rgba(201,162,39,0.95),0_0_28px_10px_rgba(201,162,39,0.5)]",
        )}
        style={token ? { borderColor: teamColor(token.team, character ? seat.alignmentOverride : null) } : undefined}
        // The seat is this player, so that is its name; character and status describe it. Without
        // an aria-label, `title` would become the accessible name.
        aria-label={seat.displayName}
        title={describeSeat(view, seat)}
      >
        {token?.icon ? (
          <img src={token.icon} alt="" className={cn("size-16 object-contain", showGuess && "opacity-50")} />
        ) : token ? (
          <span className={cn("px-1 text-center text-xs leading-tight", showGuess && "opacity-60")}>
            {token.name}
          </span>
        ) : (
          <span className="text-2xl text-parchment-dim/50">?</span>
        )}

        {!seat.alive && (
          <Skull className="absolute -right-1.5 -top-1.5 size-9 rounded-full bg-ink/90 p-1 text-parchment" />
        )}
        {/* Poisoned and drunk only ever reach a Storyteller's view, so these can never leak. They
            get a corner each: the two are different afflictions and often matter at the same time. */}
        {seat.poisoned && (
          <FlaskConical className="absolute -left-1.5 -top-1.5 size-9 rounded-full bg-ink/90 p-1 text-evil" />
        )}
        {/* A disguised seat gets its true card's icon here instead of the generic mug, so a
            Storyteller can tell the real Drunk apart at a glance from a seat merely toggled drunk by
            hand (an Innkeeper pick, a Sailor target) — both still just "drunk" to everyone else. A
            Lunatic or Marionette who is also drunk keeps the mug, moved to the bottom middle. */}
        {showSecretBadge ? (
          <img
            src={secretCard!.icon!}
            alt=""
            className="absolute -bottom-1.5 -left-1.5 size-9 rounded-full bg-ink/90 p-1 object-contain"
          />
        ) : (
          seat.drunk && (
            <Beer className="absolute -bottom-1.5 -left-1.5 size-9 rounded-full bg-ink/90 p-1 text-brass" />
          )
        )}
        {showSecretBadge && seat.drunk && seat.secretCharacterId !== "drunk" && (
          <Beer className="absolute -bottom-1.5 left-1/2 size-9 -translate-x-1/2 rounded-full bg-ink/90 p-1 text-brass" />
        )}
        {handRaised ? (
          <Hand
            className={cn(
              "absolute -bottom-1.5 -right-1.5 size-9 rounded-full bg-ink/90 p-1",
              voteLocked ? "text-brass" : "text-parchment",
            )}
          />
        ) : (
          seat.protected && (
            <Shield className="absolute -bottom-1.5 -right-1.5 size-9 rounded-full bg-ink/90 p-1 text-brass" />
          )
        )}
      </button>

      <div className="flex items-center gap-1">
        <span
          className={cn(
            "max-w-28 truncate text-sm",
            seat.alive ? "text-parchment" : "text-parchment-dim line-through",
            !seat.connected && "italic opacity-60",
          )}
          title={seat.connected ? seat.displayName : `${seat.displayName} (not connected)`}
        >
          {seat.displayName}
        </span>
        {wiki && character && (
          <a
            href={wiki}
            target="_blank"
            rel="noopener noreferrer"
            title={`Open ${character.name} on the wiki`}
            aria-label={`Open the wiki article for ${character.name}`}
            className="text-parchment-dim/60 hover:text-brass"
          >
            <BookOpen className="size-3.5" />
          </a>
        )}
        {onGuess && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onGuess();
            }}
            title="Jot a private guess about this seat"
            className="text-parchment-dim/60 hover:text-brass"
          >
            <Pencil className="size-3.5" />
          </button>
        )}
      </div>

      <div className="flex h-4 items-center gap-1 text-[11px] text-parchment-dim">
        {seat.isTraveler && <span className="rounded bg-traveler/30 px-1">Traveler</span>}
        {!seat.alive && !seat.ghostVoteUsed && <span className="rounded bg-ink-line px-1">ghost vote</span>}
        {character && seat.characterIsPublic && <span className="text-brass">{character.name}</span>}
      </div>

      {/* Storyteller only. Reseating and removing belong on the board rather than two clicks deep
          in a dialog — they are things you do *while looking at the circle*. Kept faint until the
          seat is hovered so twenty of them do not shout over the game. */}
      {showSeatTools && (
        <div
          className={cn(
            "flex h-6 items-center gap-0.5 opacity-0 transition-opacity",
            "group-hover:opacity-100 focus-within:opacity-100",
            confirmRemove && "opacity-100",
          )}
        >
          {confirmRemove ? (
            <>
              <button
                type="button"
                title={`Remove ${seat.displayName} from the game`}
                aria-label={`Confirm removing ${seat.displayName} from the game`}
                onClick={() => {
                  setConfirmRemove(false);
                  onRemove?.();
                }}
                className="rounded p-1 text-evil hover:bg-evil/20"
              >
                <Check className="size-4" />
              </button>
              <span className="text-[11px] text-evil">remove?</span>
              <button
                type="button"
                title="Cancel"
                aria-label="Cancel removing this player"
                onClick={() => setConfirmRemove(false)}
                className="rounded p-1 text-parchment-dim hover:text-parchment"
              >
                <X className="size-4" />
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                title={`Move ${seat.displayName} one seat anticlockwise`}
                aria-label={`Move ${seat.displayName} back one seat`}
                onClick={() => onMoveSeat?.(-1)}
                className="rounded p-1 text-parchment-dim hover:bg-ink-line hover:text-parchment"
              >
                <ArrowLeft className="size-4" />
              </button>
              <button
                type="button"
                title={`Move ${seat.displayName} one seat clockwise`}
                aria-label={`Move ${seat.displayName} forward one seat`}
                onClick={() => onMoveSeat?.(1)}
                className="rounded p-1 text-parchment-dim hover:bg-ink-line hover:text-parchment"
              >
                <ArrowRight className="size-4" />
              </button>
              <button
                type="button"
                title={`Remove ${seat.displayName} from the game`}
                aria-label={`Remove ${seat.displayName} from the game`}
                onClick={() => setConfirmRemove(true)}
                className="rounded p-1 text-evil/70 hover:bg-evil/20 hover:text-evil"
              >
                <Trash2 className="size-4" />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
